import React, { ComponentType, useCallback } from 'react';
import { useError } from '../components/common/ErrorHandler';
import { ApiError } from '../services/apiClient';
import { useAuth } from '../context/AuthContext';

/**
 * Higher-order component that adds API error handling to a component
 *
 * @param WrappedComponent The component to wrap with error handling
 * @returns A new component with error handling
 */
// Type for components wrapped with error handling
export interface WithErrorHandlingProps {
  handleApiCall: <T>(
    apiCall: () => Promise<T>,
    errorMessage?: string
  ) => Promise<T | null>;
}

export function withErrorHandling<P extends object>(
  WrappedComponent: ComponentType<P & WithErrorHandlingProps>
): React.FC<P> {
  const WithErrorHandling: React.FC<P> = (props) => {
    const { showError } = useError();
    const { logout } = useAuth();

    // Create a handler for API calls
    const handleApiCall = useCallback(async <T,>(
      apiCall: () => Promise<T>,
      errorMessage: string = 'An error occurred while processing your request.'
    ): Promise<T | null> => {
      try {
        return await apiCall();
      } catch (error) {
        console.error('API call error:', error);

        if (error instanceof ApiError) {
          // Handle authentication errors more gracefully
          if (error.isAuthError) {
            // Don't automatically logout - instead show a more helpful error message
            showError(
              'Your session may have expired. Please try refreshing the page or logging in again if the problem persists.', 
              'Authentication Issue'
            );
            // Don't call logout() here - let the user decide what to do
            return null;
          }

          // Show specific error message from API
          showError(error.message, 'API Error');
        } else {
          // Show generic error message
          showError(errorMessage);
        }

        return null;
      }
    }, [showError]);

    // Pass the handler to the wrapped component
    return <WrappedComponent {...props as P} handleApiCall={handleApiCall} />;
  };

  // Set display name for debugging
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithErrorHandling.displayName = `withErrorHandling(${displayName})`;

  return WithErrorHandling;
}

export default withErrorHandling;
